/**
 * Itinerary Table -- Stop-by-stop route detail when a route is selected.
 * Fetches from API (getRouteItinerary) with local fallback computation.
 *
 * API response shape:
 *   { day, technician, shift_start_hour, service_minutes, total_stops,
 *     total_travel_s, total_service_s, total_distance_mi,
 *     itinerary: [{ seq, type, address, lat, lon, arrival_s, depart_s,
 *                    travel_s, service_s, distance_mi }] }
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as api from '../services/routeOptimizerService';
import '../styles/ItineraryTable.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format seconds-from-shift-start into 12h time (e.g. "8:30 AM"). */
function formatTime(totalSeconds, startHour) {
  const totalMin = startHour * 60 + Math.round(totalSeconds / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/** Format duration in seconds to human-readable (e.g. "2 min", "1h 30m"). */
function formatDuration(seconds) {
  if (seconds == null) return '\u2014';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

/**
 * Replicate the backend travel-time model (nonlinear speed function).
 * Uses the same constants as DemoWebGUI app.py _travel_time_seconds.
 */
function travelTimeSeconds(distanceMiles) {
  if (distanceMiles < 0.001) return 0;
  const parking = 27.65;
  const minSpeed = 11.79;
  const maxSpeed = 34.85;
  const expRate = 0.031;
  const logScale = 6.67;
  const expComponent = maxSpeed - (maxSpeed - minSpeed) * Math.exp(-expRate * distanceMiles);
  const logBonus = logScale * Math.log(distanceMiles + 1);
  const effectiveSpeed = Math.min(Math.max(expComponent + logBonus, minSpeed), maxSpeed * 1.5);
  return parking + (distanceMiles / effectiveSpeed) * 3600;
}

/** Haversine distance between two [lat, lon] coords, in miles. */
function haversineDistanceMi(a, b) {
  if (!a || !b || a[0] == null || b[0] == null) return 0;
  const R = 3958.8; // Earth radius in miles
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLon = ((b[1] - a[1]) * Math.PI) / 180;
  const lat1 = (a[0] * Math.PI) / 180;
  const lat2 = (b[0] * Math.PI) / 180;
  const hav =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(hav));
}

// ---------------------------------------------------------------------------
// Local itinerary computation (fallback when API unavailable)
// ---------------------------------------------------------------------------

function computeLocalItinerary(route, coords, settings, addressLabels) {
  if (!route || !route.stops?.length) return null;

  const shiftStartHour = settings?.shift_hours ?? 8;
  const serviceMinutes = settings?.service_minutes ?? 20;
  const serviceS = serviceMinutes * 60;
  const depotCoord = coords?.['0'] || [28.9275, -82.0373];
  const stops = route.stops;

  const itinerary = [];

  // Depot start
  itinerary.push({
    seq: 0,
    type: 'depot',
    address: 'Depot (Start)',
    lat: depotCoord[0],
    lon: depotCoord[1],
    arrival_s: 0,
    travel_s: 0,
    service_s: 0,
    depart_s: 0,
    distance_mi: 0,
  });

  let cumul = 0;
  let prevCoord = depotCoord;

  for (let i = 0; i < stops.length; i++) {
    const si = stops[i];
    const coord = coords?.[String(si)] || [null, null];
    const distMi = haversineDistanceMi(prevCoord, coord);
    const travelS = travelTimeSeconds(distMi);
    cumul += travelS;

    itinerary.push({
      seq: i + 1,
      type: 'stop',
      address: (() => { const lbl = addressLabels?.[String(si)]; return typeof lbl === 'string' ? lbl : lbl?.text || `Stop ${si}`; })(),
      lat: coord[0],
      lon: coord[1],
      arrival_s: Math.round(cumul),
      travel_s: Math.round(travelS),
      service_s: serviceS,
      depart_s: Math.round(cumul + serviceS),
      distance_mi: Math.round(distMi * 100) / 100,
    });

    cumul += serviceS;
    prevCoord = coord[0] != null ? coord : prevCoord;
  }

  // Insert lunch break if configured (find first gap in the window)
  const lunchDuration = parseInt(settings?.lunch_break_duration_minutes) || 0;
  if (lunchDuration > 0) {
    const lunchS = lunchDuration * 60;
    const startHour = shiftStartHour;
    const earliest = settings.lunch_break_earliest || '11:00';
    const latest = settings.lunch_break_latest || '14:00';
    const [eH, eM] = earliest.split(':').map(Number);
    const [lH, lM] = latest.split(':').map(Number);
    const earlyS = ((eH - startHour) * 3600) + (eM * 60);
    const lateS = ((lH - startHour) * 3600) + (lM * 60);

    // Find insertion point: first stop whose arrival_s is in the window
    let insertIdx = -1;
    for (let i = 1; i < itinerary.length; i++) {
      if (itinerary[i].arrival_s >= earlyS && itinerary[i].arrival_s <= lateS) {
        insertIdx = i;
        break;
      }
    }
    if (insertIdx > 0) {
      const prevStop = itinerary[insertIdx - 1];
      itinerary.splice(insertIdx, 0, {
        seq: 0, type: 'lunch',
        address: 'Lunch Break',
        lat: prevStop.lat, lon: prevStop.lon,
        arrival_s: prevStop.depart_s || prevStop.arrival_s,
        depart_s: (prevStop.depart_s || prevStop.arrival_s) + lunchS,
        travel_s: 0, service_s: lunchS, distance_mi: 0,
      });
      // Shift subsequent arrival/depart times
      for (let i = insertIdx + 1; i < itinerary.length; i++) {
        itinerary[i].arrival_s += lunchS;
        if (itinerary[i].depart_s) itinerary[i].depart_s += lunchS;
      }
      cumul += lunchS;
    }
  }

  // Insert chem refill if configured (around middle of stops)
  const chemLat = settings?.chem_refill_lat ? parseFloat(settings.chem_refill_lat) : null;
  const chemLon = settings?.chem_refill_lon ? parseFloat(settings.chem_refill_lon) : null;
  if (chemLat != null && chemLon != null) {
    const chemDuration = parseInt(settings?.chem_refill_duration_minutes) || 15;
    const chemS = chemDuration * 60;
    const chemAddr = settings?.chem_refill_location || settings?.chem_refill_address || 'Chemical Refill Station';
    // Insert after the middle stop
    const midIdx = Math.floor(itinerary.length / 2);
    const prevStop = itinerary[midIdx];
    const distToChem = haversineDistanceMi(
      [prevStop.lat, prevStop.lon], [chemLat, chemLon]
    );
    const travelToChem = travelTimeSeconds(distToChem);
    itinerary.splice(midIdx + 1, 0, {
      seq: 0, type: 'chem_refill',
      address: chemAddr,
      lat: chemLat, lon: chemLon,
      arrival_s: Math.round((prevStop.depart_s || prevStop.arrival_s) + travelToChem),
      depart_s: Math.round((prevStop.depart_s || prevStop.arrival_s) + travelToChem + chemS),
      travel_s: Math.round(travelToChem), service_s: chemS,
      distance_mi: Math.round(distToChem * 100) / 100,
    });
    const addedTime = travelToChem + chemS;
    for (let i = midIdx + 2; i < itinerary.length; i++) {
      itinerary[i].arrival_s += Math.round(addedTime);
      if (itinerary[i].depart_s) itinerary[i].depart_s += Math.round(addedTime);
    }
    cumul += addedTime;
  }

  // Renumber sequences
  itinerary.forEach((row, i) => { row.seq = i; });

  // Return to depot
  const returnDistMi = haversineDistanceMi(prevCoord, depotCoord);
  const returnTravelS = travelTimeSeconds(returnDistMi);
  cumul += returnTravelS;

  itinerary.push({
    seq: itinerary.length,
    type: 'depot',
    address: 'Depot (Return)',
    lat: depotCoord[0],
    lon: depotCoord[1],
    arrival_s: Math.round(cumul),
    travel_s: Math.round(returnTravelS),
    service_s: 0,
    depart_s: Math.round(cumul),
    distance_mi: Math.round(returnDistMi * 100) / 100,
  });

  return {
    day: route.day,
    technician: route.technician_name,
    shift_start_hour: shiftStartHour,
    service_minutes: serviceMinutes,
    itinerary,
    total_stops: stops.length,
    total_travel_s: Math.round(itinerary.reduce((s, r) => s + (r.travel_s || 0), 0)),
    total_service_s: serviceS * stops.length,
    total_distance_mi:
      Math.round(itinerary.reduce((s, r) => s + (r.distance_mi || 0), 0) * 100) / 100,
  };
}

// Styles moved to ../styles/ItineraryTable.css (.ro-it-* classes)

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ItineraryTable({ weekId, selectedRoute, coords, solution, settings, addressLabels }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Find matching route from solution for local fallback
  const matchedRoute = useMemo(() => {
    if (!selectedRoute || !solution?.routes) return null;
    return solution.routes.find(
      (r) => r.day === selectedRoute.day && r.technician_name === selectedRoute.technician
    );
  }, [selectedRoute, solution]);

  const fetchItinerary = useCallback(async () => {
    if (!weekId || !selectedRoute) return;

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const result = await api.getRouteItinerary(weekId, {
        day: selectedRoute.day,
        technician: selectedRoute.technician,
      });

      if (result && result.itinerary) {
        // Enrich API response with address labels if available
        if (addressLabels && matchedRoute?.stops) {
          result.itinerary.forEach((row, i) => {
            if (row.type !== 'depot' && matchedRoute.stops[i - 1] != null) {
              const si = matchedRoute.stops[i - 1];
              const labelObj = addressLabels[String(si)];
              const label = typeof labelObj === 'string' ? labelObj : labelObj?.text;
              if (label && (!row.address || row.address === `Stop ${si}` || row.address === `Address #${si}`)) {
                row.address = label;
              }
            }
          });
        }
        setData(result);
      } else {
        throw new Error('No itinerary in response');
      }
    } catch (err) {
      console.warn('API itinerary fetch failed, using local fallback:', err.message);
      // Local fallback
      if (matchedRoute) {
        const local = computeLocalItinerary(matchedRoute, coords, settings, addressLabels);
        if (local) {
          setData(local);
        } else {
          setError('Could not compute itinerary for this route.');
        }
      } else {
        setError('Route not found in solution data.');
      }
    } finally {
      setLoading(false);
    }
  }, [weekId, selectedRoute, matchedRoute, coords, settings, addressLabels]);

  useEffect(() => {
    fetchItinerary();
  }, [fetchItinerary]);

  // --- Empty state ---
  if (!selectedRoute) {
    return (
      <div className="ro-it-wrapper">
        <div className="ro-it-empty">Click a route card above to view its stop-by-stop itinerary</div>
      </div>
    );
  }

  // --- Loading ---
  if (loading) {
    return (
      <div className="ro-it-wrapper">
        <div className="ro-it-header">
          <span>
            {selectedRoute.technician} — {selectedRoute.day}
          </span>
        </div>
        <div className="ro-it-loading" role="status" aria-live="polite">Loading itinerary...</div>
      </div>
    );
  }

  // --- Error ---
  if (error) {
    return (
      <div className="ro-it-wrapper">
        <div className="ro-it-header">
          <span>
            {selectedRoute.technician} — {selectedRoute.day}
          </span>
        </div>
        <div className="ro-it-error" role="alert">
          {error}
          <button
            type="button"
            onClick={fetchItinerary}
            className="ro-btn ro-btn-sm ro-btn-outline-danger ro-it-retry-btn"
          >Retry</button>
        </div>
      </div>
    );
  }

  // --- No data ---
  if (!data || !data.itinerary) {
    return (
      <div className="ro-it-wrapper">
        <div className="ro-it-header">
          <span>
            {selectedRoute.technician} — {selectedRoute.day}
          </span>
        </div>
        <div className="ro-it-empty">No itinerary data available.</div>
      </div>
    );
  }

  const sh = data.shift_start_hour || 8;
  const itinerary = data.itinerary;
  const lastRow = itinerary[itinerary.length - 1];
  const finishTime = lastRow ? formatTime(lastRow.arrival_s, sh) : '\u2014';

  return (
    <div className="ro-it-wrapper">
      {/* Header */}
      <div className="ro-it-header">
        <span>
          {data.technician || selectedRoute.technician} \u2014{' '}
          {data.day || selectedRoute.day}
        </span>
      </div>

      {/* Table */}
      <div className="ro-it-table-wrap">
        <table className="ro-it-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Address</th>
              <th>Lat</th>
              <th>Lon</th>
              <th>Arrival</th>
              <th>Depart</th>
              <th>Travel</th>
              <th>Service</th>
              <th>Distance</th>
            </tr>
          </thead>
          <tbody>
            {itinerary.map((stop) => {
              const isDepot = stop.type === 'depot';
              const isLunch = stop.type === 'lunch';
              const isChem = stop.type === 'chem_refill';
              const isStart = isDepot && stop.seq === 0;
              const isReturn = isDepot && stop.seq > 0;
              const isSpecial = isLunch || isChem;

              const arrival = formatTime(stop.arrival_s, sh);
              const depart = isReturn ? '\u2014' : formatTime(stop.depart_s ?? stop.arrival_s, sh);
              const travel = isStart ? '\u2014' : formatDuration(stop.travel_s);
              const service = isDepot ? '\u2014' : formatDuration(stop.service_s);
              const dist = isStart
                ? '\u2014'
                : `${(stop.distance_mi ?? 0).toFixed(1)} mi`;
              const lat = stop.lat != null ? stop.lat.toFixed(4) : '\u2014';
              const lon = stop.lon != null ? stop.lon.toFixed(4) : '\u2014';

              const rowClass = isDepot ? 'ro-itinerary-depot-row'
                : isLunch ? 'ro-itinerary-lunch-row'
                : isChem ? 'ro-itinerary-chem-row'
                : 'ro-itinerary-stop-row';

              const icon = isLunch ? '\u{1F37D}\uFE0F ' : isChem ? '\u{1F9EA} ' : '';

              return (
                <tr key={stop.seq} className={rowClass}>
                  <td>{isSpecial ? '' : stop.seq}</td>
                  <td className="ro-it-addr" title={stop.address}>{icon}{stop.address}</td>
                  <td>{lat}</td>
                  <td>{lon}</td>
                  <td className="ro-it-time">{arrival}</td>
                  <td className="ro-it-time">{depart}</td>
                  <td>{travel}</td>
                  <td>{service}</td>
                  <td>{dist}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary Footer */}
      <div className="ro-it-summary">
        <span>{data.total_stops ?? itinerary.length - 2} stops</span>
        <span>{(data.total_distance_mi ?? 0).toFixed(1)} miles</span>
        <span>{formatDuration(data.total_travel_s)} driving</span>
        <span>{formatDuration(data.total_service_s)} service</span>
        <span>Finish: {finishTime}</span>
      </div>
    </div>
  );
}
